import { useState, useCallback } from 'react';
import { Content, Comment } from '../types/content';
import ContentService from '../services/ContentService';
import CommentService from '../services/CommentService';
import { useApi } from './useApi';

interface UseContentProps {
  contentType: string;
  contentId: string;
}

export function useContent({ contentType, contentId }: UseContentProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const {
    data: content,
    loading: contentLoading,
    error: contentError,
    execute: fetchContent
  } = useApi(
    () => ContentService.getContentById(contentType, contentId),
    {
      onError: (error) => {
        console.error('Failed to fetch content:', error);
      }
    }
  );

  const {
    loading: commentsLoading,
    error: commentsError,
    execute: fetchCommentsPage
  } = useApi(
    async () => {
      const response = await CommentService.getComments(contentType, contentId, {
        page,
        limit: 10,
        sort: 'createdAt',
        order: 'desc'
      });
      
      if (page === 1) {
        setComments(response.data);
      } else {
        setComments(prev => [...prev, ...response.data]);
      }
      
      setHasMore(page < response.meta.totalPages);
      return response;
    }
  );

  const loadMoreComments = useCallback(() => {
    if (!commentsLoading && hasMore) {
      setPage(prev => prev + 1);
      fetchCommentsPage();
    }
  }, [commentsLoading, hasMore, fetchCommentsPage]);

  const {
    loading: addingComment,
    execute: executeAddComment
  } = useApi(
    (comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt' | 'likes' | 'likedBy'>) =>
      CommentService.createComment(contentType, contentId, comment),
    {
      onSuccess: (newComment) => {
        setComments(prev => [newComment, ...prev]);
      }
    }
  );

  const {
    loading: likingContent,
    execute: executeLikeContent
  } = useApi(
    () => ContentService.likeContent(contentType, contentId),
    {
      onSuccess: () => {
        if (content) {
          const updatedContent = {
            ...content,
            likes: content.likes + 1,
            likedBy: [...(content.likedBy || []), 'current-user']
          };
          // Update local content state
          fetchContent();
        }
      }
    }
  );

  const {
    loading: likingComment,
    execute: executeLikeComment
  } = useApi(
    (commentId: string) => CommentService.likeComment(commentId),
    {
      onSuccess: (_, [commentId]) => {
        setComments(prev =>
          prev.map(comment =>
            comment.id === commentId
              ? { ...comment, likes: comment.likes + 1, likedBy: [...comment.likedBy, 'current-user'] }
              : comment
          )
        );
      }
    }
  );

  return {
    content,
    comments,
    hasMore,
    loading: {
      content: contentLoading,
      comments: commentsLoading,
      addingComment,
      likingContent,
      likingComment
    },
    error: {
      content: contentError,
      comments: commentsError
    },
    actions: {
      fetchContent,
      loadMoreComments,
      addComment: executeAddComment,
      likeContent: executeLikeContent,
      likeComment: executeLikeComment
    }
  };
}